import React from 'react'
import { useSelector } from 'react-redux'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'
import { useMediaQuery, useTheme } from '@mui/material'
import { designTokens } from '@/theme'
import { useMainScrollHeaderCollapse } from '@/hooks/useMainScrollHeaderCollapse'
import { HeaderCollapseProgressContext } from '@/context/header-collapse-context'
import { DESKTOP_TOOLBAR_SPACER_PX, getMobileToolbarSpacerPx } from '@/layouts/header-metrics'
import { Outlet, useLocation } from 'react-router-dom'
import Topbar from '@/layouts/topbar'
import LoadingView from '@/components/loading-view'
import { RootState } from '@/store/store'
import Footer from '@/layouts/footer'
import { pagesWithNavigation } from '@/utils/app-helper'
import { useMenuItems } from '@/hooks/use-menu-items'
import { logMenuDiag } from '@/utils/menu-load-diagnostics'
import NoInternetConnection from '@/components/no-internet-connection'
import { DetailModalProvider } from '@/context/detail-modal-context'
import { MenuItemDetailsProvider } from '@/context/menu-item-details-context'
import { CartLockRecoveryProvider } from '@/context/cart-lock-recovery-context'
import BottomStack from '@/layouts/BottomStack'
import { PaymentGatewayListener } from '@/components/payment-gateway-listener'
import { BillingPreviewSyncProvider } from '@/context/BillingPreviewSyncProvider'
import { stripStorefrontPrefix } from '@/utils/storefront-paths'

function AppLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const mobileCollapseProgress = useMainScrollHeaderCollapse(isMobile && !prefersReducedMotion, true)
  const toolbarSpacerPx = !isMobile
    ? DESKTOP_TOOLBAR_SPACER_PX
    : getMobileToolbarSpacerPx(mobileCollapseProgress)
  const processState = useSelector((state: RootState) => state.processState.working)
  const { pathname } = useLocation()
  const appPath = stripStorefrontPrefix(pathname)

  const isCheckoutRoute = appPath === '/checkout' || appPath.startsWith('/checkout/')
  /** Recover has no CheckoutLayoutShell scroller; do not inherit checkout's overflow:hidden clip. */
  const isCheckoutRecoverRoute = appPath === '/checkout/recover'
  const lockCheckoutViewport = isCheckoutRoute && !isCheckoutRecoverRoute
  const isLoginRoute = appPath === '/login'
  /** Hide shared topbar logo on login for phones only — card keeps the brand mark. */
  const isNarrowMobile = useMediaQuery('(max-width:767.95px)')
  const isMobileLoginRoute = isNarrowMobile && isLoginRoute
  const isMobileImmersiveBrowseRoute = isMobile && (appPath === '/dishes' || appPath === '/')
  const showMainTopbar =
    !isCheckoutRoute && !isMobileImmersiveBrowseRoute && !isMobileLoginRoute

  const { fetchMenuItems } = useMenuItems()

  React.useEffect(() => {
    // Homepage product ADD reuses MenuItemDetailModal and needs the catalog loaded.
    logMenuDiag('app_layout_pathname_effect', {
      pathname,
      appPath,
      willFetchMenu: true,
    })
    fetchMenuItems()
  }, [fetchMenuItems, pathname, appPath])

  return window?.navigator?.onLine ? (
    <BillingPreviewSyncProvider>
      <DetailModalProvider>
      <CartLockRecoveryProvider>
      <MenuItemDetailsProvider>
      <CssBaseline />
      {showMainTopbar ? <Topbar mobileCollapseProgress={mobileCollapseProgress} /> : null}
      {showMainTopbar ? (
        <Toolbar
          sx={{
            height: toolbarSpacerPx,
            minHeight: toolbarSpacerPx,
          }}
        />
      ) : null}
      <Box
        id="mainContainer"
        component="main"
        className={isMobileLoginRoute ? 'login-page' : undefined}
        sx={{
          height: showMainTopbar ? 'calc(100vh - 64px)' : '100dvh',
          pb: !isCheckoutRoute && pagesWithNavigation?.some((el) => el === appPath) ? { xs: '132px', md: '0px' } : '0px',
          overflowY: lockCheckoutViewport ? 'hidden' : 'auto',
          overflowX: 'hidden',
          scrollbarGutter: 'stable',
          minWidth: 0,
          // Let login's warm gradient fill the viewport (no gray header band).
          background: isMobileLoginRoute ? 'transparent' : theme.palette.background.default,
          borderRadius: isMobileLoginRoute
            ? 0
            : `${designTokens.shape.borderRadius.xl}px ${designTokens.shape.borderRadius.xl}px 0 0`,
          '& > *': {
            minWidth: 0,
          },
          '& .px': {
            px: 2,
          },
        }}
      >
        {window?.navigator?.onLine ? (
          <HeaderCollapseProgressContext.Provider
            value={isMobile && !prefersReducedMotion ? mobileCollapseProgress : 0}
          >
            <Box
              sx={{
                maxWidth: 1200,
                mx: 'auto',
                px: isMobileLoginRoute ? 0 : 2,
                width: '100%',
                height: lockCheckoutViewport || isMobileLoginRoute ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: lockCheckoutViewport ? 0 : '100%',
                overflow: 'visible',
                minWidth: 0,
              }}
            >
              <Outlet context="main" />
            </Box>
          </HeaderCollapseProgressContext.Provider>
        ) : (
          <NoInternetConnection />
        )}

        {!isCheckoutRoute && pagesWithNavigation?.some((el) => el === appPath) && <Footer />}
      </Box>
      <PaymentGatewayListener />
      <BottomStack />
      {processState && <LoadingView />}
      </MenuItemDetailsProvider>
      </CartLockRecoveryProvider>
      </DetailModalProvider>
    </BillingPreviewSyncProvider>
  ) : (
    <NoInternetConnection />
  )
}

export default AppLayout
